import { useMemo } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetMeQuery } from '../../../store/user/user'
import { useZocksAuth, useZocksQuota } from '../../../components/ScopedBlock/hooks'

import { OAuth2AppPermissionsBody, useSetOAuth2AppPermissionsMutation, useGetOAuth2AppByClientIdQuery } from '../../../store/auth/auth'
import { useAppDispatch } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { toBooleanSelectionMap } from './utils'

type UseOAuth2AppAccessStateParams = {
  clientId: string
  refetchOnMountOrArgChange?: boolean
}

export const useAppAccessState = ({ clientId, refetchOnMountOrArgChange = false }: UseOAuth2AppAccessStateParams) => {
  const accountId = useAppSelector(selectAccountId)
  const { hasScope } = useZocksAuth()
  const { hasQuota, validateQuotaValue } = useZocksQuota()

  const { data: userData, isLoading: userIsLoading, isFetching: userIsFetching } = useGetMeQuery({ accountId }, { refetchOnMountOrArgChange })

  const {
    data: oAuth2AppData,
    isLoading: appIsLoading,
    isFetching: appIsFetching,
  } = useGetOAuth2AppByClientIdQuery({ accountId, clientId }, { refetchOnMountOrArgChange })

  const isPending = userIsLoading || appIsLoading || userIsFetching || appIsFetching

  const currentIntegration = useMemo(() => {
    return userData?.integrations?.find((i) => i.serviceId === clientId)
  }, [userData, clientId])

  const hasAccessToApp = useMemo(() => {
    if (!oAuth2AppData || !userData) {
      return false
    }

    const accountEnabled = oAuth2AppData.permissions.account
    const userRoles = userData.roles.map((role) => role.id)

    const activeSystemRoles = Object.entries(oAuth2AppData.permissions.systemRoles)
      .filter(([, value]) => value)
      .map(([key]) => key)

    const activeAccountRoles = Object.entries(oAuth2AppData.permissions.accountRoles)
      .filter(([, value]) => value)
      .map(([key]) => key)

    return userRoles.some((role) => activeSystemRoles.includes(role) || activeAccountRoles.includes(role)) && accountEnabled
  }, [oAuth2AppData, userData])

  const isEnabled = useMemo(() => {
    if (!oAuth2AppData) {
      return false
    }

    return oAuth2AppData.permissions.account
  }, [oAuth2AppData])

  const hasPermissionUpdateScope = hasScope('**', 'oauth2_app:update')
  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')
  const appNotBlacklisted = validateQuotaValue('integration.oauth2AppBlacklist', clientId)

  return {
    currentIntegration,
    hasAccessToApp,
    isEnabled,
    hasPermissionUpdateScope,
    oauth2Enabled,
    appNotBlacklisted,
    isPending,
  }
}

interface ToastConfig {
  title?: string
  messages: string[]
}

interface UseOAuth2AppRolePermissionsParams {
  accountId: string
  clientId: string
  accountEnabled: boolean
  successToast?: ToastConfig
  errorToast?: ToastConfig
}

interface UpdatePermissionsParams {
  selectedRoleIds: string[]
  allSystemRoles: string[]
}

interface UpdatePermissionsSuccessResult {
  ok: true
  body: OAuth2AppPermissionsBody
}

interface UpdatePermissionsErrorResult {
  ok: false
}

type UpdatePermissionsResult = UpdatePermissionsSuccessResult | UpdatePermissionsErrorResult

export const useOAuth2AppRolePermissions = ({ accountId, clientId, accountEnabled, successToast, errorToast }: UseOAuth2AppRolePermissionsParams) => {
  const dispatch = useAppDispatch()

  const [updateOAuth2AppPermissions, { isLoading: isUpdating }] = useSetOAuth2AppPermissionsMutation()

  const buildPermissionsBody = (selectedRoleIds: string[], allSystemRoles: string[]): OAuth2AppPermissionsBody => {
    const allSystemRolesSet = new Set(allSystemRoles)
    const selectedSystemRoleIds = selectedRoleIds.filter((roleId) => allSystemRolesSet.has(roleId))
    const selectedSystemRoleIdSet = new Set(selectedSystemRoleIds)
    const selectedAccountRoleIds = selectedRoleIds.filter((roleId) => !selectedSystemRoleIdSet.has(roleId))

    return {
      account: accountEnabled,
      systemRoles: toBooleanSelectionMap(allSystemRoles, selectedSystemRoleIds),
      accountRoles: toBooleanSelectionMap(selectedAccountRoleIds, selectedAccountRoleIds), //use this later to fill with account specific roles
    }
  }

  const updatePermissions = async ({ selectedRoleIds, allSystemRoles }: UpdatePermissionsParams): Promise<UpdatePermissionsResult> => {
    try {
      const body = buildPermissionsBody(selectedRoleIds, allSystemRoles)
      const response = await updateOAuth2AppPermissions({
        accountId,
        clientId,
        body,
      })
      if (response.error) {
        if (errorToast) {
          dispatch(
            addToast({
              variant: 'error',
              title: errorToast.title,
              messages: errorToast.messages,
            }),
          )
        }
        return { ok: false }
      }
      if (successToast) {
        dispatch(
          addToast({
            variant: 'success',
            title: successToast.title,
            messages: successToast.messages,
          }),
        )
      }
      return {
        ok: true,
        body,
      }
    } catch (error) {
      console.error(error)
      if (errorToast) {
        dispatch(
          addToast({
            variant: 'error',
            title: errorToast.title,
            messages: errorToast.messages,
          }),
        )
      }

      return { ok: false }
    }
  }
  return {
    isUpdating,
    buildPermissionsBody,
    updatePermissions,
  }
}
